import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState, useCallback, useRef } from "react";
import type { Swiper as SwiperClass } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Scrollbar, Navigation, Mousewheel } from "swiper/modules";
import classNames from "classnames/bind";
import styles from "./StaticPageNavigation.module.scss";
import "swiper/css";
import "swiper/css/scrollbar";

const cx = classNames.bind(styles);

type Page = {
    urlSlug: string;
    pageName: string;
    url: string;
    codename: string;
};

type Section = {
    codename: string;
    name: string;
    url: string;
    homeTitle: string;
    homeLink: string;
    pages: Page[];
    children: Section[];
};

type ComponentProps = {
    parentUrl: string;
    url: string;
    pages: Page[];
    homeTitle: string;
    homeLink: string;
    children: Section[];
    activePath: string[];
    setActivePaths: () => void; //just triggers an update, requires no params or return val
    maxRows: number;
    currentUrl: string;
};

const RecursiveRow = ({
    parentUrl,
    url,
    pages,
    homeTitle,
    homeLink,
    children,
    activePath: activePaths,
    setActivePaths,
    maxRows,
    currentUrl,
}: ComponentProps) => {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const swiperRef = useRef<SwiperClass | null>(null);
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const [hoveredLabelIndex, setHoveredLabelIndex] = useState<number | null>(null);
    // Render after mount/on client so that when swiper is trying to set props to do with breakpoints, it has access to the initialised window.innerWidth
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const checkForScrollbar = () => {
        // if the swiper scroll bar is available and is actually scrollable e.g. has overflow, set styles so it appears below the swiper slides
        const wrapper = rowRef.current?.querySelector(".swiper-wrapper") as HTMLElement;
        const container = rowRef.current?.querySelector(".swiper") as HTMLElement;
        const scrollbar = rowRef.current?.querySelector(".swiper-scrollbar") as HTMLElement;

        if (wrapper && container && scrollbar) {
            const isScrollable = wrapper.scrollWidth > container.clientWidth;
            setHasScrollbar(isScrollable);

            if (window.innerWidth >= 1024) {
                scrollbar.classList.remove(styles.scrollbarDisabled);
                if (isScrollable) scrollbar.classList.add(styles.scrollbarDesktopSpacing);
            } else if (window.innerWidth < 1024) {
                scrollbar.classList.remove(styles.scrollbarDesktopSpacing);
                scrollbar.classList.add(styles.scrollbarDisabled);
            }
        }
    };

    const getRowLevelFromUrl = (url: string) => {
        return url.split("/").length - 2;
    };
    const rowLevel = getRowLevelFromUrl(url);

    if (rowLevel >= maxRows) return null;

    const isActive = activePaths.includes(url);
    const visibility = isActive ? "visible" : "hidden";

    const slideToCurrentSlide = () => {
        if (!!swiperRef.current) {
            const activeIndex = swiperRef.current.clickedIndex;
            if (swiperRef.current.previousIndex != activeIndex) {
                swiperRef.current?.slideTo(activeIndex);
            }
        }
    };

    const labelOnClick = () => {
        setActivePaths();
        slideToCurrentSlide();
    };

    const extractFirstNumber = (codename: string) => {
        const match = codename.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
    };

    pages.sort(
        (a: { codename: string }, b: { codename: string }) =>
            extractFirstNumber(a.codename) - extractFirstNumber(b.codename)
    );

    return isClient && isActive ? (
        <>
            <div
                className={cx("row", { "showing-scrollbar": hasScrollbar })}
                data-parent-url={parentUrl}
                data-row-level={rowLevel}
                ref={rowRef}
            >
                <Swiper
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        // Wait for DOM to settle and set scrollbar class
                        setTimeout(() => {
                            swiper.update();
                            checkForScrollbar();
                        }, 0);
                    }}
                    className={cx("swiperElement")}
                    freeMode={true}
                    slidesPerView="auto"
                    modules={[FreeMode, Scrollbar, Navigation, Mousewheel]}
                    key={url}
                    style={{ visibility: visibility }}
                    mousewheel={{
                        enabled: true,
                        forceToAxis: true,
                    }}
                    navigation={{
                        enabled: true,
                        disabledClass: cx("navigationDisabled"),
                        nextEl: "#navigationFadeNext",
                        prevEl: "#navigationFadePrev",
                    }}
                    scrollbar={{
                        enabled: false,
                        draggable: false,
                    }}
                    breakpoints={{
                        1024: {
                            scrollbar: {
                                hide: false,
                                enabled: true,
                                draggable: true,
                            },
                        },
                    }}
                >
                    <div
                        className={cx("navigationFade", "navigationFadeBeginning")}
                        id="navigationFadePrev"
                    ></div>
                    {rowLevel === 0 && homeTitle && homeLink && (
                        <SwiperSlide
                            className={cx("swiper-label", {
                                active: currentUrl === homeLink,
                                "next-is-active": activePaths.includes(pages[0].url),
                                "hovered-previous":
                                    hoveredLabelIndex != undefined && hoveredLabelIndex != null
                                        ? hoveredLabelIndex == 0
                                        : false,
                            })}
                            key={homeLink}
                            data-element-key={homeLink}
                            data-row-level={rowLevel}
                            onClick={() => labelOnClick()}
                            onMouseEnter={() => setHoveredLabelIndex(0)}
                            onMouseLeave={() => setHoveredLabelIndex(null)}
                        >
                            <Link
                                href={homeLink}
                                className={cx({ active: currentUrl === homeLink })}
                            >
                                {homeTitle}
                            </Link>
                        </SwiperSlide>
                    )}
                    {pages.map(
                        (
                            element: {
                                url: string;
                                pageName: string;
                            },
                            index: number
                        ) => {
                            const rowLevel = getRowLevelFromUrl(element.url.toString());
                            const elementKey = element.url;
                            const isActiveLabel = activePaths.includes(element.url.toString());
                            const nextElement = pages[index + 1];
                            const isNextActive =
                                nextElement && activePaths.includes(nextElement.url);
                            return (
                                <SwiperSlide
                                    className={cx("swiper-label", {
                                        active: isActiveLabel,
                                        "next-is-active": isNextActive,
                                        "hovered-previous":
                                            hoveredLabelIndex != undefined &&
                                            hoveredLabelIndex != null
                                                ? index == hoveredLabelIndex - 1
                                                : false,
                                    })}
                                    key={elementKey.toString()}
                                    data-element-key={elementKey}
                                    data-row-level={rowLevel}
                                    onClick={() => labelOnClick()}
                                    onMouseEnter={() => setHoveredLabelIndex(index)}
                                    onMouseLeave={() => setHoveredLabelIndex(null)}
                                >
                                    <Link
                                        href={element.url}
                                        className={cx({ active: isActiveLabel })}
                                    >
                                        {element.pageName}
                                    </Link>
                                </SwiperSlide>
                            );
                        }
                    )}
                    <div
                        className={cx("navigationFade", "navigationFadeEnd")}
                        id="navigationFadeNext"
                    ></div>
                </Swiper>
            </div>
            {children?.map((child) => {
                return (
                    <RecursiveRow
                        parentUrl={parentUrl}
                        currentUrl={currentUrl}
                        key={child.url}
                        activePath={activePaths}
                        setActivePaths={setActivePaths}
                        maxRows={maxRows}
                        {...child}
                    ></RecursiveRow>
                );
            })}
        </>
    ) : null;
};

const StaticPageNavigation = (props: any) => {
    const { activePath, maxRows, currentUrl, ...rowProps } = props.props;

    const router = useRouter();
    const getActiveRoutes = useCallback(() => {
        const activePages = router.asPath.split("/").filter((item) => item !== "");
        const basePath = "/";

        const activePaths = activePages.reduce(
            (accumulator: string[], segment: string, index: number) => {
                const currentPath = basePath + activePages.slice(0, index + 1).join("/");
                accumulator.push(currentPath);
                return accumulator;
            },
            []
        );

        return activePaths;
    }, [router.asPath]);

    /*
        Stores the active paths as a list of urls which match the url of the element which should be active 
        e.g. if the user is at this location: /plc/investor-relations/corporate-governance 
        the stored values would be ["/plc", "/plc/investor-relations", "/plc/investor-relations/corporate-governance"]
    */
    const [activePaths, setActivePaths] = useState(getActiveRoutes);

    useEffect(() => {
        setActivePaths(getActiveRoutes());
    }, [getActiveRoutes, router.asPath]);

    return (
        <div className={cx("wrapper")}>
            <RecursiveRow
                data-parent-url={"/"}
                setActivePaths={() => setActivePaths(getActiveRoutes())}
                activePath={activePaths}
                maxRows={3}
                currentUrl={router.asPath}
                {...rowProps}
            ></RecursiveRow>
        </div>
    );
};

export default StaticPageNavigation;
